import { ClientRouteOptions, TypedClientAjaxProxy } from "@cervval.js/client-html5-spa/routes";
import { showErrorInDialog } from "@cervval.js/client-html5-ui/src/components/dialog/dialog";
import { DTOCacheRepository } from "@cervval.js/common-action/src/dtos/dtoCacheRepository";
import { ActionCacheFetchDataRoutes } from "@cervval.js/common-action/src/routes/actions.route";
import { ACTION_CACHE_SERVICE } from "@cervval.js/common-action/src/services/cache.service";
import { DefaultActionCacheService } from "@cervval.js/common-action/src/services/impl/default.cache.service";
import { FetchDataParams, FetchDataResults } from "@cervval.js/common-sql-core";
import { Injectable } from "@cervval.js/common/src/dependencies/injectable";
import { ErrorWithCode } from "@cervval.js/common/src/errors/errors";
import { getLogConsole } from "@cervval.js/common/src/services/log.service";
import { DebugCjs, DebugHandler } from "@cervval.js/common/src/tools/debug.decorator";
import { anyReallyAny } from "@cervval.js/common/tools";

export type LoadDataStrategy = "append" | "replace";
export interface DirtyCacheHandler<FetchDataTypes, CacheInfo> {
    /** 
     * @returns false if no fetch is needed / adjusted variables depending on current repository content and determine load data strategy otherwise
     */
    isDirty?<FetchUID extends keyof FetchDataTypes>(repository: DTOCacheRepository<anyReallyAny, CacheInfo>, uid: FetchUID, variables: FetchDataTypes[FetchUID]): { variables: FetchDataTypes[FetchUID], strategy: LoadDataStrategy, newCacheInfo?: CacheInfo } | false;

    /** append or replace data within repository */
    applyLoadStrategy?<T>(repository: DTOCacheRepository<T, CacheInfo>, data: T[], strategy: LoadDataStrategy, newCacheInfo: CacheInfo | undefined, setDataDateMs: number): void;
}

/**
 * Append or replace data within repository
 */
export function defaultApplyLoadStrategy<T, CacheInfo>(repository: DTOCacheRepository<T, CacheInfo>, data: T[], strategy: LoadDataStrategy, newCacheInfo: CacheInfo, setDataDateMs: number): void {
    if (strategy === "append") {
        // append new rows
        const allRows = [...repository.dtoList, ...data];
        repository.setData(allRows, newCacheInfo, setDataDateMs);
    } else {
        // replace all rows
        repository.setData(data, newCacheInfo, setDataDateMs);
    }
}

/** Check if repository is dirty and return default (replace) strategy */
export function defaultIsDirty<Vars>(repository: DTOCacheRepository<anyReallyAny, anyReallyAny>, uid: unknown, variables: Vars): { variables: Vars, strategy: LoadDataStrategy, newCacheInfo?: undefined } | false {
    if (repository.isDirty()) {
        return { strategy: "replace", variables, newCacheInfo: undefined };
    }
    return false;
}

@Injectable(ACTION_CACHE_SERVICE)
export class ClientActionCacheService<ElementTypes, FetchDataTypes, CacheInfo = unknown> extends DefaultActionCacheService<ElementTypes, CacheInfo> {

    @DebugCjs()
    protected debug!: DebugHandler;

    @TypedClientAjaxProxy()
    public fetchRoutes!: ActionCacheFetchDataRoutes<ClientRouteOptions>;

    /** Application dependant functions that handle the cache info and dirty states */
    public dirtyCacheHandler?: DirtyCacheHandler<FetchDataTypes, CacheInfo>;

    /**
     * Fetch data from database
     */
    public fetchDataImpl<FetchUID extends keyof FetchDataTypes>(params: FetchDataParams<FetchUID, FetchDataTypes[FetchUID], keyof ElementTypes>, routesOptions?: ClientRouteOptions): Promise<FetchDataResults> {
        return this.fetchRoutes.fetchData<FetchUID, FetchDataTypes[FetchUID], keyof ElementTypes>(params, routesOptions);
    }

    /**
     * Fetch data if not already in cache or marked dirty
     * @returns the fetched tables
     * @param routeOptions : Containing abortController that allow to abort the query
     * @exemple
     * ```
     * this._abortController = new AbortController();
     * ...
     * await fetchDataIfNeeded(..., {abortController : this._abortController});
     * ...
     * this._abortController.abort(); // Will cancel the queries instancied with this abortController
     * ``` 
     */
    public fetchDataIfNeeded<FetchUID extends keyof FetchDataTypes>(
        uid: FetchUID, variables: FetchDataTypes[FetchUID], tables: (keyof ElementTypes)[], routeOptions? : ClientRouteOptions): Promise<{ fetchedTables: (keyof ElementTypes)[] }> {

        const retValue: { fetchedTables: (keyof ElementTypes)[] } = { fetchedTables: [] };

        const isDirty = this.dirtyCacheHandler?.isDirty ?? defaultIsDirty;
        const applyLoadData = this.dirtyCacheHandler?.applyLoadStrategy ?? defaultApplyLoadStrategy;

        // map of types to fetch by variables set
        const requestedTypesByVarsUid: Map<string, { variables: FetchDataTypes[FetchUID], types: { type: keyof ElementTypes, strategy: LoadDataStrategy, newCacheInfo: CacheInfo | undefined }[] }> = new Map();
        for (const t of tables) {
            const repository = this.getDTOCacheRepository(t);
            const isDirtyResult = isDirty(repository, uid, variables);
            if (isDirtyResult) {
                const varsUid = JSON.stringify(isDirtyResult.variables);
                let req = requestedTypesByVarsUid.get(varsUid);
                if (req == null) {
                    // this is a new set of variables
                    req = { variables: isDirtyResult.variables, types: [] };
                    requestedTypesByVarsUid.set(varsUid, req);
                }
                req.types.push({ type: t, strategy: isDirtyResult.strategy, newCacheInfo: isDirtyResult.newCacheInfo });
                retValue.fetchedTables.push(t);
            }
        }
        if (requestedTypesByVarsUid.size === 0) {
            // nothing to fetch...
            return Promise.resolve(retValue);
        }

        this.debug("fetchData", requestedTypesByVarsUid);

        const setDataDateMs = Date.now();// record date now so that all dirty cache messages that come in the meanwhile are considered

        return Promise.all(Array.from(requestedTypesByVarsUid.values()).map((req) => {
            return this.fetchDataImpl<FetchUID>({
                uid, variables: req.variables, requested: req.types.map(t => { return { type: t.type }; })
            }, routeOptions).then((result) => {
                for (const d of result.data) {
                    const repo = this.findDTOCacheRepository(d.type as keyof ElementTypes);
                    if (repo != null) {
                        const dReq = req.types.find((t) => t.type === d.type);
                        if (dReq == null) {
                            getLogConsole().error("Server returned not requested data", d.type, " ?");
                            continue;
                        }
                        // unmark dirty and setData
                        applyLoadData(repo, d.rows as (ElementTypes[keyof ElementTypes])[], dReq.strategy, dReq.newCacheInfo, setDataDateMs);
                        if (d.error) {
                            repo.markDirty();
                        }
                    } else {
                        getLogConsole().error(new Error("Repository not found for " + d.type));
                    }
                }
                if (result.errors?.length) {
                    const error: ErrorWithCode = {
                        code: 580,// SQL error
                        debugInfos: { details: "\n" + result.errors.join("\n") }
                    };
                    showErrorInDialog(error);
                }
            });
        })).then(() => {
            // notify items may not be dirty anymore (for refresh button)
            this.dirtyStateChanged.emit();

            return retValue;
        });
    }
}
